import {getCodeLabel} from './lib/dictionary';
import {CollapsibleSection} from './CollapsibleSection';
import {SegmentGroup} from './SegmentGroup';
import {buildSections, countIssues, groupBasePaths} from './lib/sections';
import type {Section, SectionGroup} from './lib/sections';
import type {EdiElementValue, EdiSegment} from './lib/tokenizer';
import type {EdiTransactionExplained} from './lib/explainedEnvelope';
import type {ElementPath} from './lib/document-editor';
import type {ValidationFinding} from './lib/validator';

interface DataSectionsProps {
  groupIndex: number;
  transactionIndex: number;
  transaction: EdiTransactionExplained;
  findings: ValidationFinding[];
  onEdit: (path: ElementPath, value: string) => void;
}

function text(value: EdiElementValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function find(group: SectionGroup, id: string): EdiSegment | undefined {
  return group.entries.find(e => e.segment.id === id)?.segment;
}

function plural(count: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : pluralNoun}`;
}

/** The one-line summary shown beside a section's title while collapsed. */
function sectionPreview(section: Section): string {
  const entries = section.groups.flatMap(g => g.entries);
  switch (section.key) {
    case 'order': {
      const beg = find(section.groups[0], 'BEG');
      if (beg) {
        const po = text(beg.elements[2]);
        return po ? `PO ${po}` : '';
      }
      const bct = text(find(section.groups[0], 'BCT')?.elements[2]);
      return bct ? `Report ${bct}` : '';
    }
    case 'parties':
      return plural(section.groups.length, 'party', 'parties');
    case 'items':
      return plural(section.groups.length, 'line');
    case 'dates':
      return plural(entries.length, 'date');
    case 'notes':
      return plural(entries.length, 'note');
    case 'totals': {
      const total = text(find(section.groups[0], 'AMT')?.elements[1]);
      return total ? `Total ${total}` : '';
    }
    default:
      return plural(entries.length, 'entry', 'entries');
  }
}

function partyTitle(group: SectionGroup): {title: string; preview: string} {
  const n1 = find(group, 'N1');
  const n4 = find(group, 'N4');
  const code = text(n1?.elements[0]);
  const place = [text(n4?.elements[0]), text(n4?.elements[1])]
    .filter(Boolean)
    .join(', ');
  return {
    title: code ? getCodeLabel('N1', 1, code) : 'Party',
    preview: [text(n1?.elements[1]), place].filter(Boolean).join(' · '),
  };
}

function lineTitle(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const po1 = find(group, 'PO1');
  const qty = [text(po1?.elements[1]), text(po1?.elements[2])]
    .filter(Boolean)
    .join(' ');
  const price = text(po1?.elements[3]);
  return {
    title: `Line ${text(po1?.elements[0]) || index + 1}`,
    preview: [
      text(po1?.elements[6]),
      qty && `Qty ${qty}`,
      price && `@ ${price}`,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The 852 equivalent of lineTitle — an item-activity (LIN) line's quantity
 * lives on its own QTY segment(s) rather than on LIN itself, so this reads
 * from the group's first QTY entry rather than from LIN's own elements. */
function lineTitle852(
  group: SectionGroup,
  index: number
): {title: string; preview: string} {
  const lin = find(group, 'LIN');
  const qty = find(group, 'QTY');
  const qtyValue = text(qty?.elements[1]);
  const qtyUom = text(qty?.elements[2]);
  const qtyText = qtyValue
    ? `Qty ${qtyValue}${qtyUom ? ` ${qtyUom}` : ''}`
    : '';
  return {
    title: `Line ${text(lin?.elements[0]) || index + 1}`,
    preview: [text(lin?.elements[2]), qtyText].filter(Boolean).join(' · '),
  };
}

/**
 * The active 850 or 852 transaction, organized by kind of data (see
 * lib/sections.ts) as collapsible panels. Parties and line/activity items
 * are one collapsible row each. Every collapsed header shows its
 * error/warning counts up front.
 */
export function DataSections({
  groupIndex,
  transactionIndex,
  transaction,
  findings,
  onEdit,
}: DataSectionsProps) {
  const sections = buildSections(transaction, groupIndex, transactionIndex);
  const is852 = Boolean(transaction.parsed852);

  const renderEntries = (group: SectionGroup) => (
    <div className="divide-y divide-border/60">
      {group.entries.map(({segment, basePath}) => (
        <SegmentGroup
          key={basePath}
          segment={segment}
          basePath={basePath}
          groupIndex={groupIndex}
          transactionIndex={transactionIndex}
          transaction={transaction}
          findings={findings}
          onEdit={onEdit}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-2">
      {sections.map(section => (
        <CollapsibleSection
          key={section.key}
          title={section.title}
          preview={sectionPreview(section)}
          tooltip={section.description}
          issues={countIssues(findings, groupBasePaths(section.groups))}
        >
          {section.repeating ? (
            <div className="space-y-1.5">
              {section.groups.map((group, i) => {
                const {title, preview} =
                  section.key === 'parties'
                    ? partyTitle(group)
                    : is852
                      ? lineTitle852(group, i)
                      : lineTitle(group, i);
                return (
                  <CollapsibleSection
                    key={group.key}
                    level="row"
                    title={title}
                    preview={preview}
                    issues={countIssues(findings, groupBasePaths([group]))}
                  >
                    {renderEntries(group)}
                  </CollapsibleSection>
                );
              })}
            </div>
          ) : (
            renderEntries(section.groups[0])
          )}
        </CollapsibleSection>
      ))}
    </div>
  );
}
